import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, SlidersHorizontal, Grid3x3, Info, Upload, Download, AlertTriangle, XCircle } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized, getApiFieldError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSkillLevelSimplified } from '@/hooks/useSkillLevelSimplified';
import { FilePickerField } from '@/components/FilePickerField';
import { MasterHistoryPanel } from '@/components/masters/MasterHistoryPanel';

/**
 * `/masters/skills` (v0.4 章④ F-3、D-25/D-64。人・組織機能 増分② で拡張、D-607/D-649)。
 * ⚠️ D-649: 画面上の表記は「業務 / スキル（業務×抽象度） / 習熟度 / 人材像 / 役職・等級」の5タブに
 * 統一する (8タブにしない — 320px でタブ行が横オーバーフローする)。「面」「尺度」という
 * ドラフト用語は画面には出さない。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-6 / §4-11
 *       docs/design/v04-people-org-requirements-detail.md §3-2 / §5 / §6
 */

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const textareaCls = 'w-full px-3 py-2 rounded border border-surface-border focus-ring';

// =============================================================================
// 共通: code/label/sort_order (+任意の1追加フィールド) だけのフラットマスタ (業務・役職等級)
// =============================================================================
type SimpleMaster = { id: string; code: string; label: string; sort_order: number };
type ExtraFieldConfig = { key: 'group_label'; label: string; maxLength: number };

type SimpleMasterDef = {
  key: 'business-functions' | 'position-levels';
  idField: string;
  title: string;
  description: string;
  extraField?: ExtraFieldConfig;
  /** v0.5 マスタ変更履歴ビューア フェーズ1 (docs/design/v05-master-history-viewer-requirements.md §2-3):
   *  詳細表示する列と日本語ラベル (表示順)。code/label/sort_order は全エンティティ共通。 */
  historyFieldLabels: Record<string, string>;
};

const SIMPLE_MASTER_DEFS: SimpleMasterDef[] = [
  {
    key: 'business-functions', idField: 'business_function_id', title: '業務',
    description: '各社が定義する業務の単位を登録します (例: 発注、仕込み、接客)。「スキル（業務×抽象度）」タブで業務と抽象度を組み合わせてスキルを作ります。',
    extraField: { key: 'group_label', label: 'グループ (任意、例: 店舗管理/店舗オペレーション)', maxLength: 100 },
    historyFieldLabels: { code: 'コード', label: '名称', group_label: 'グループ', sort_order: '並び順' },
  },
  {
    key: 'position-levels', idField: 'position_level_id', title: '役職・等級',
    description: '役職・等級の順序を登録します。可視性ルール等で「役職の上下」を判定する際に使われます。',
    historyFieldLabels: { code: 'コード', label: '名称', sort_order: '並び順' },
  },
];

type MasterRow = { code: string; label: string; sort_order: number } & Record<string, unknown>;
type MasterDraft = { id: string | null; code: string; label: string; sort_order: string; extra: string; effective_from: string };

const emptyDraft = (sortOrder: number): MasterDraft => ({ id: null, code: '', label: '', sort_order: String(sortOrder), extra: '', effective_from: '' });

/** v0.5 マスタ開始日指定機能: 新規作成・改定の効力発生日を任意で指定する共通フィールド。
 *  空欄のままなら保存した瞬間 (サーバー時刻) から有効になる (従来通りの挙動)。
 *  常に空欄から始める (既存行のeffective_fromを編集フォームに逆算表示すると、改定のたびに
 *  「前回と同じ日付」が既定値になり、複数回の改定が同日扱いで拒否される事故を招くため)。
 *  ⚠️ 未来日付を指定しても「予約」ではなく保存した瞬間に現行版として反映される
 *  (employees.ts の稼働開始日と同じ既存の制限、§4参照)。案内文はこの実態に合わせている。 */
function EffectiveFromField({ value, onChange, error, disabled }: {
  value: string; onChange: (v: string) => void; error?: string; disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm text-text-sub mb-1">開始日 (任意)</label>
      <input type="date" value={value} disabled={disabled} aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={cn(inputCls, error && 'border-danger-border')} />
      <p className="text-xs text-text-sub mt-1">
        空欄の場合は保存した時点からすぐに有効になります。指定した日付に関わらず、保存した瞬間に現行版として
        反映されます (未来日付を指定しても、実際にその日になるまで待って切り替わるわけではありません)。
        過去日付での遡及記録にご利用ください。
      </p>
      {error && <p role="alert" className="text-xs text-danger-text mt-1">{error}</p>}
    </div>
  );
}

function SimpleMasterTab({ def, canManage }: { def: SimpleMasterDef; canManage: boolean }) {
  const qc = useQueryClient();
  const queryKey = [def.key];
  const [draft, setDraft] = useState<MasterDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SimpleMaster | null>(null);

  const { data, isLoading } = useQuery<MasterRow[]>({
    queryKey, queryFn: () => apiFetch<MasterRow[]>(`/masters/${def.key}`),
  });
  const rows = (data ?? []).map((r) => ({ id: String((r as Record<string, unknown>)[def.idField]), code: r.code, label: r.label, sort_order: r.sort_order })) as SimpleMaster[];
  const rawByCode = data ?? [];

  const saveMut = useMutation({
    mutationFn: (d: MasterDraft) => {
      const payload: Record<string, unknown> = { code: d.code.trim(), label: d.label.trim(), sort_order: Number(d.sort_order) || 0 };
      if (def.extraField) payload[def.extraField.key] = d.extra.trim() || null;
      if (d.effective_from) payload.effective_from = d.effective_from;
      return d.id
        ? apiFetch(`/masters/${def.key}/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch(`/masters/${def.key}`, { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/${def.key}/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub">{def.description}</p>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyDraft(rows.length))}>
            {def.title}を追加
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : rows.length === 0 ? (
        <EmptyState message={`${def.title}がまだありません`} description={canManage ? `「${def.title}を追加」から登録してください。` : undefined} />
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => {
            const raw = rawByCode[i] as MasterRow;
            const extraValue = def.extraField ? (raw[def.extraField.key] as string | null) : null;
            return (
              <li key={r.id} className="flex items-center justify-between gap-2 border border-surface-border rounded-lg bg-white p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-body break-words">{r.label}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{r.code}</span>
                    {extraValue && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap">{extraValue}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <MasterHistoryPanel entityRoute={`/masters/${def.key}`} id={r.id} label={def.title} fieldLabels={def.historyFieldLabels} />
                  {canManage && (
                    <>
                      <button type="button" aria-label="編集" onClick={() => setDraft({
                        id: r.id, code: r.code, label: r.label, sort_order: String(r.sort_order), extra: extraValue ?? '', effective_from: '',
                      })} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                      <button type="button" aria-label="削除" onClick={() => setDeleteTarget(r)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.id ? `${def.title}を編集` : `${def.title}を追加`} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={100} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">並び順</label>
              <input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className={inputCls} />
            </div>
            {def.extraField && (
              <div>
                <label className="block text-sm text-text-sub mb-1">{def.extraField.label}</label>
                <input value={draft.extra} onChange={(e) => setDraft({ ...draft, extra: e.target.value })} maxLength={def.extraField.maxLength} className={inputCls} />
              </div>
            )}
            <EffectiveFromField value={draft.effective_from} onChange={(v) => setDraft({ ...draft, effective_from: v })}
              error={getApiFieldError(saveMut.error, 'effective_from')} />
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title={`${def.title}を削除`} message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)} />
    </div>
  );
}

// =============================================================================
// スキル（業務×抽象度）タブ (人・組織機能 増分② E3、既存「スキル」タブの拡張、改称しない D-649)
// =============================================================================
type SkillFace = {
  skill_id: string; code: string; label: string; description: string | null; sort_order: number;
  business_function_id: string | null; abstraction_level: string | null; scale_id: string | null; is_enabled: boolean;
};
type BusinessFunction = { business_function_id: string; code: string; label: string; group_label: string | null; sort_order: number };
type EvaluationScale = { scale_id: string; code: string; label: string; is_locked: boolean };
// D-720 (2026-09-06): 抽象度は固定3値からテナント別マスタへ移行 (§13-3)。
type AbstractionLevel = { level_id: string; code: string; label: string; sort_order: number };
type AbstractionLevelDraft = { id: string | null; code: string; label: string; sort_order: string };
const UNCLASSIFIED_FILTER = '__unclassified__';

type SkillBaseDraft = { id: string | null; code: string; label: string; description: string; sort_order: string; effective_from: string; scaleId: string | null };
type SkillFaceDraft = { id: string; businessFunctionId: string; abstractionLevel: string; scaleId: string; isEnabled: boolean };

/** v0.5 (docs/design/v05-skill-level-criteria-detail.md §4): スキル×習熟度の到達基準文。 */
type SkillLevelCriterion = { skill_level_id: string; criteria_text: string };

/** v0.5 マスタ変更履歴ビューア フェーズ1 §2-3: そのバージョンの全フィールド値の表示順・ラベル。
 *  ⚠️ 実装後レビュー (design-and-code) で発見: master-writers/skills.ts の改定 (revise) 経路が
 *  業務ID/抽象度コード/評価尺度ID/有効フラグ (「面」情報) を新バージョンへ引き継いでいなかった
 *  (改定のたびに business_function_id 等がリセットされるデータ損失バグ)。別セッションで実測確認・
 *  修正済み (統合テスト追加、production等の既存データへの実害は無いことを確認済み)。
 *  修正後は履歴表示に含めても当時の実態を正しく表す。 */
const SKILL_HISTORY_FIELD_LABELS: Record<string, string> = {
  code: 'コード', label: '名称', description: '説明', sort_order: '並び順',
  business_function_id: '業務ID', abstraction_level: '抽象度コード', scale_id: '評価尺度ID', is_enabled: '有効フラグ',
};

function SkillsFaceTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [baseDraft, setBaseDraft] = useState<SkillBaseDraft | null>(null);
  const [faceDraft, setFaceDraft] = useState<SkillFaceDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SkillFace | null>(null);
  const [filterBusinessFunctionId, setFilterBusinessFunctionId] = useState('');
  // <768px ドリルダウン (D-647) 用: 「すべて」を明示選択したか。filterBusinessFunctionId は
  // 「すべて」も「未選択」も同じ空文字列で表す (≥768px のグリッドではそれで良い) ため、
  // モバイルの「まず業務を選択してください」ガードだけはこの別フラグで判定する
  // (空文字列を共用すると「すべて」を選んでも未選択のまま扱われるバグになる)。
  // ⚠️ 「すべて」は Select の初期値と同じ表示のため、開いてすぐ「すべて」を選ぶと Radix の
  // onValueChange は値が変化しないため発火しない。onOpenChange (閉じた時点) でも touched にする。
  const [filterTouched, setFilterTouched] = useState(false);
  const [abstractionLevelsModalOpen, setAbstractionLevelsModalOpen] = useState(false);
  const [abstractionLevelForm, setAbstractionLevelForm] = useState<AbstractionLevelDraft | null>(null);
  const [deleteAbstractionLevelTarget, setDeleteAbstractionLevelTarget] = useState<AbstractionLevel | null>(null);
  // 5並列レビューHIGH是正 (D-720): 最大保持数の編集フォーム用ドラフト (null = 未編集)。
  const [maxCountDraft, setMaxCountDraft] = useState<string | null>(null);

  const { data: skills = [], isLoading } = useQuery<SkillFace[]>({ queryKey: ['skills'], queryFn: () => apiFetch('/masters/skills') });
  const { data: businessFunctions = [] } = useQuery<BusinessFunction[]>({ queryKey: ['business-functions'], queryFn: () => apiFetch('/masters/business-functions') });
  const { data: scales = [] } = useQuery<EvaluationScale[]>({ queryKey: ['evaluation-scales'], queryFn: () => apiFetch('/masters/evaluation-scales') });
  const { data: abstractionLevels = [] } = useQuery<AbstractionLevel[]>({ queryKey: ['abstraction-levels'], queryFn: () => apiFetch('/masters/abstraction-levels') });
  // v0.5 (docs/design/v05-skill-level-criteria-detail.md §4): 「スキルを編集」モーダルの
  // 「到達基準」セクション用。段階ラベル・汎用行動記述 (placeholder) は尺度単位で共通のため
  // skill-levels タブと同じ queryKey を共有する (キャッシュ再利用、別々に取得しない)。
  const { data: skillLevels = [] } = useQuery<SkillLevel[]>({ queryKey: ['skill-levels'], queryFn: () => apiFetch('/masters/skill-levels') });
  const [criteriaDraft, setCriteriaDraft] = useState<Record<string, string>>({});
  // ⚠️ [実装後レビューで是正、platform-reviewer Major]: 保存は「本体PUT→到達基準PUT」の
  // 2回の独立したAPI呼び出しで、1回目成功・2回目失敗の部分失敗がありうる。この状態でそのまま
  // 再試行すると、dirty-checkの無い本体PUT (skills.ts writer) が同じ内容でも毎回SCD-2改定行を
  // 作ってしまう (§3-2が避けようとした「無意味な改定の積み上がり」が部分失敗の再試行経路から
  // 発生する)。本体PUTが成功済みならこのidを覚えておき、再試行時は到達基準PUTのみ再送する。
  const [baseSavedPendingCriteria, setBaseSavedPendingCriteria] = useState<string | null>(null);
  const skillDetailQuery = useQuery<SkillFace & { level_criteria: SkillLevelCriterion[] }>({
    queryKey: ['skill-detail', baseDraft?.id],
    queryFn: () => apiFetch(`/masters/skills/${baseDraft!.id}`),
    enabled: !!baseDraft?.id,
  });
  useEffect(() => {
    setCriteriaDraft(Object.fromEntries((skillDetailQuery.data?.level_criteria ?? []).map((c) => [c.skill_level_id, c.criteria_text])));
  }, [skillDetailQuery.data]);
  // D-720 (5並列レビューHIGH是正): 最大保持数は routes/skill-masters.ts の
  // resolveAbstractionLevelMaxCount と同じデフォルト値 (20) をフォールバックに使う。
  const { data: tenantSettings } = useQuery<{ abstraction_level_max_count?: number }>({
    queryKey: ['tenant-settings'], queryFn: () => apiFetch('/tenant-settings'),
  });
  const abstractionLevelMaxCount = tenantSettings?.abstraction_level_max_count ?? 20;
  const abstractionLevelAtLimit = abstractionLevels.length >= abstractionLevelMaxCount;

  const bfLabel = (id: string | null) => businessFunctions.find((b) => b.business_function_id === id)?.label ?? id;
  const scaleLabel = (id: string | null) => scales.find((s) => s.scale_id === id)?.label ?? id;
  const abstractionLevelLabel = (code: string | null) => abstractionLevels.find((a) => a.code === code)?.label ?? code;
  // design-expert指摘: 削除確認ダイアログに参照件数を事前提示する。skills は既にこの画面で
  // 全件取得済みのため、参照専用エンドポイントを新設せずクライアント側で集計する。
  const abstractionLevelUsageCount = (code: string) => skills.filter((s) => s.abstraction_level === code).length;

  const abstractionLevelSaveMut = useMutation({
    mutationFn: (d: AbstractionLevelDraft) => {
      const payload: Record<string, unknown> = { label: d.label.trim(), sort_order: Number(d.sort_order) || 0 };
      if (!d.id) payload.code = d.code.trim();
      return d.id
        ? apiFetch(`/masters/abstraction-levels/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/masters/abstraction-levels', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['abstraction-levels'] }); setAbstractionLevelForm(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const abstractionLevelDeleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/abstraction-levels/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['abstraction-levels'] }); setDeleteAbstractionLevelTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteAbstractionLevelTarget(null); },
  });

  // 5並列レビューHIGH是正 (D-720): テナントごとの抽象度最大保持数を編集するミューテーション。
  // 権限は abstraction-levels の CRUD と同じ skill:manage (全店舗スコープ、= canManage) で保護する
  // (apps/api/src/routes/tenant-settings.ts KEY_PERMISSION_OVERRIDES と対応)。
  const maxCountSaveMut = useMutation({
    mutationFn: (value: number) =>
      apiFetch('/tenant-settings/abstraction_level_max_count', { method: 'PUT', body: JSON.stringify({ value }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenant-settings'] }); setMaxCountDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const maxCountDraftValue = maxCountDraft ?? String(abstractionLevelMaxCount);
  const canSaveMaxCount = Number.isInteger(Number(maxCountDraftValue)) && Number(maxCountDraftValue) >= 1
    && Number(maxCountDraftValue) >= abstractionLevels.length;

  const canSaveAbstractionLevel = !!abstractionLevelForm && abstractionLevelForm.label.trim() !== ''
    && (abstractionLevelForm.id !== null || abstractionLevelForm.code.trim() !== '');

  const filteredSkills = skills.filter((s) => {
    if (!filterBusinessFunctionId) return true;
    if (filterBusinessFunctionId === UNCLASSIFIED_FILTER) return !s.business_function_id;
    return s.business_function_id === filterBusinessFunctionId;
  });

  const saveMut = useMutation({
    mutationFn: async (d: SkillBaseDraft) => {
      const payload: Record<string, unknown> = { code: d.code.trim(), label: d.label.trim(), description: d.description.trim() || null, sort_order: Number(d.sort_order) || 0 };
      if (d.effective_from) payload.effective_from = d.effective_from;
      if (d.id) {
        // 部分失敗からの再試行では本体PUTを再送しない (baseSavedPendingCriteria参照、上記コメント)。
        if (baseSavedPendingCriteria !== d.id) {
          await apiFetch(`/masters/skills/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        }
        // v0.5 (docs/design/v05-skill-level-criteria-detail.md §3-2/§4): 到達基準は専用の
        // PUT /skills/:id/level-criteria で別途置換する (既存 PUT /skills/:id には混在させない)。
        // scale_id 未設定のスキルはセクション自体を表示していないため呼ばない。
        if (d.scaleId) {
          const levelCriteria: SkillLevelCriterion[] = Object.entries(criteriaDraft)
            .map(([skillLevelId, text]) => [skillLevelId, text.trim()] as const)
            .filter(([, text]) => text !== '')
            .map(([skillLevelId, text]) => ({ skill_level_id: skillLevelId, criteria_text: text }));
          try {
            await apiFetch(`/masters/skills/${d.id}/level-criteria`, {
              method: 'PUT', body: JSON.stringify({ level_criteria: levelCriteria }),
            });
          } catch (e) {
            setBaseSavedPendingCriteria(d.id);
            throw new Error(e instanceof ApiClientError
              ? `基本情報は保存されましたが、到達基準の保存に失敗しました: ${e.message}`
              : '基本情報は保存されましたが、到達基準の保存に失敗しました。');
          }
        }
      } else {
        await apiFetch('/masters/skills', { method: 'POST', body: JSON.stringify(payload) });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skills'] });
      qc.invalidateQueries({ queryKey: ['skill-detail'] });
      setBaseDraft(null);
      setBaseSavedPendingCriteria(null);
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/skills/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['skills'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const faceMut = useMutation({
    mutationFn: (d: SkillFaceDraft) => apiFetch(`/masters/skills/${d.id}/face`, {
      method: 'PUT',
      body: JSON.stringify({
        business_function_id: d.businessFunctionId || null,
        abstraction_level: d.abstractionLevel || null,
        scale_id: d.scaleId || null,
        is_enabled: d.isEnabled,
      }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['skills'] }); setFaceDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const canSaveBase = !!baseDraft && baseDraft.code.trim() !== '' && baseDraft.label.trim() !== '';
  const canSaveFace = !!faceDraft && (!!faceDraft.businessFunctionId === !!faceDraft.abstractionLevel);

  const openBaseCreate = () => { setBaseSavedPendingCriteria(null); setBaseDraft({ id: null, code: '', label: '', description: '', sort_order: String(skills.length), effective_from: '', scaleId: null }); };
  const openBaseEdit = (s: SkillFace) => { setBaseSavedPendingCriteria(null); setBaseDraft({ id: s.skill_id, code: s.code, label: s.label, description: s.description ?? '', sort_order: String(s.sort_order), effective_from: '', scaleId: s.scale_id }); };
  const openFaceEdit = (s: SkillFace) => setFaceDraft({
    id: s.skill_id, businessFunctionId: s.business_function_id ?? '', abstractionLevel: s.abstraction_level ?? '',
    scaleId: s.scale_id ?? '', isEnabled: s.is_enabled,
  });

  const renderRowMeta = (s: SkillFace) => (
    <div className="flex items-center gap-1.5 flex-wrap mt-1">
      {s.business_function_id ? (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap">{bfLabel(s.business_function_id)}</span>
      ) : (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">未分類</span>
      )}
      {s.abstraction_level && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
          {abstractionLevelLabel(s.abstraction_level)}
        </span>
      )}
      {s.scale_id && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">尺度: {scaleLabel(s.scale_id)}</span>
      )}
      {!s.is_enabled && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">無効</span>
      )}
    </div>
  );

  const rowActions = (s: SkillFace) => (
    <div className="flex items-center gap-1 shrink-0">
      <MasterHistoryPanel entityRoute="/masters/skills" id={s.skill_id} label="スキル" fieldLabels={SKILL_HISTORY_FIELD_LABELS} />
      {canManage && (
        <>
          <button type="button" aria-label="編集" onClick={() => openBaseEdit(s)} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
          <button type="button" aria-label="業務・抽象度の設定" onClick={() => openFaceEdit(s)} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><SlidersHorizontal size={14} /></button>
          <button type="button" aria-label="削除" onClick={() => setDeleteTarget(s)} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub">
          スキル (業務×抽象度) を登録します。業務・抽象度・評価尺度は「業務・抽象度の設定」から後付けできます (未設定のままでも保存できます、未分類は正常な状態です)。
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" onClick={() => setAbstractionLevelsModalOpen(true)}>抽象度を管理</Button>
          {canManage && (
            <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={openBaseCreate}>
              スキルを追加
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-xs">
        <label className="block text-xs text-text-sub mb-1">業務で絞り込み</label>
        <Select value={filterBusinessFunctionId || '__all__'}
          onValueChange={(v) => { setFilterBusinessFunctionId(v === '__all__' ? '' : v); setFilterTouched(true); }}
          onOpenChange={(open) => { if (!open) setFilterTouched(true); }}>
          <SelectTrigger aria-label="業務で絞り込み"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">すべて</SelectItem>
            <SelectItem value={UNCLASSIFIED_FILTER}>未分類</SelectItem>
            {businessFunctions.map((b) => <SelectItem key={b.business_function_id} value={b.business_function_id}>{b.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : skills.length === 0 ? (
        <EmptyState message="スキルがまだありません" description={canManage ? '「スキルを追加」から登録してください。' : undefined} />
      ) : (
        <>
          {/* ≥768px: 一覧グリッド (D-647) */}
          <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-surface-sunken">
                    <th scope="col" className="relative px-3 py-2 text-left text-xs text-text-sub border-b border-surface-border">名称</th>
                    <th scope="col" className="relative px-3 py-2 text-left text-xs text-text-sub border-b border-surface-border">コード</th>
                    <th scope="col" className="relative px-3 py-2 text-left text-xs text-text-sub border-b border-surface-border">業務・抽象度・尺度</th>
                    <th scope="col" className="relative px-3 py-2 text-right text-xs text-text-sub border-b border-surface-border">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSkills.map((s) => (
                    <tr key={s.skill_id} className="odd:bg-white even:bg-surface-stripe">
                      <td className="px-3 py-2 align-top">
                        <div className="font-semibold text-text-body break-words">{s.label}</div>
                        {s.description && <p className="text-xs text-text-sub mt-0.5 break-words">{s.description}</p>}
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-text-sub whitespace-nowrap">{s.code}</td>
                      <td className="px-3 py-2 align-top">{renderRowMeta(s)}</td>
                      <td className="px-3 py-2 align-top"><div className="flex justify-end">{rowActions(s)}</div></td>
                    </tr>
                  ))}
                  {filteredSkills.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-text-sub">該当するスキルがありません</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* <768px: 業務を選択してドリルダウン (D-647) */}
          <div className="md:hidden space-y-2">
            {!filterTouched ? (
              <p className="text-sm text-text-sub">上の「業務で絞り込み」からまず業務を選択してください。</p>
            ) : filteredSkills.length === 0 ? (
              <EmptyState message="該当するスキルがありません" />
            ) : (
              <ul className="space-y-2">
                {filteredSkills.map((s) => (
                  <li key={s.skill_id} className="border border-surface-border rounded-lg bg-white p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-text-body break-words">{s.label}</span>
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{s.code}</span>
                        </div>
                        {renderRowMeta(s)}
                      </div>
                      {rowActions(s)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <Modal open={!!baseDraft} title={baseDraft?.id ? 'スキルを編集' : 'スキルを追加'} onClose={() => setBaseDraft(null)} size="md"
        footer={baseDraft && (
          <>
            <Button variant="secondary" onClick={() => setBaseDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveBase || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(baseDraft)}>保存</Button>
          </>
        )}>
        {baseDraft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={baseDraft.code} onChange={(e) => setBaseDraft({ ...baseDraft, code: e.target.value })} maxLength={50} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={baseDraft.label} onChange={(e) => setBaseDraft({ ...baseDraft, label: e.target.value })} maxLength={100} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">並び順</label>
              <input type="number" value={baseDraft.sort_order} onChange={(e) => setBaseDraft({ ...baseDraft, sort_order: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">説明 (任意)</label>
              <textarea value={baseDraft.description} onChange={(e) => setBaseDraft({ ...baseDraft, description: e.target.value })} maxLength={500} rows={2} className={textareaCls} />
            </div>
            <EffectiveFromField value={baseDraft.effective_from} onChange={(v) => setBaseDraft({ ...baseDraft, effective_from: v })}
              error={getApiFieldError(saveMut.error, 'effective_from')} />

            {/* v0.5 (docs/design/v05-skill-level-criteria-detail.md §4): 「到達基準」セクション。
                既存4アイコン列への5個目のボタン追加ではなく、このモーダル内に統合する (design-expert指摘)。 */}
            <div className="pt-2 border-t border-surface-border">
              <h3 className="text-sm font-semibold text-text-body mb-1">到達基準</h3>
              {!baseDraft.scaleId ? (
                <p className="text-xs text-text-sub">業務・抽象度の設定で評価尺度を選択すると、ここでスキル別の到達基準を入力できます。</p>
              ) : skillDetailQuery.isLoading ? (
                <p className="text-xs text-text-sub">読み込み中…</p>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-text-sub">
                    段階ごとに、このスキル固有の到達基準を入力します (未入力の段階は習熟度タブの共通の行動記述が使われます)。この基準文の変更履歴は記録されません。
                  </p>
                  {skillLevels.filter((l) => l.scale_id === baseDraft.scaleId).map((level) => (
                    <div key={level.skill_level_id}>
                      <label htmlFor={`skill-level-criteria-${level.skill_level_id}`} className="block text-sm text-text-sub mb-1">{level.label}</label>
                      <textarea id={`skill-level-criteria-${level.skill_level_id}`}
                        value={criteriaDraft[level.skill_level_id] ?? ''}
                        onChange={(e) => setCriteriaDraft({ ...criteriaDraft, [level.skill_level_id]: e.target.value })}
                        maxLength={500} rows={2} placeholder={level.behavior_description ?? ''} className={textareaCls} />
                    </div>
                  ))}
                  {skillLevels.filter((l) => l.scale_id === baseDraft.scaleId).length === 0 && (
                    <p className="text-xs text-text-sub">この評価尺度にはまだ段階がありません。「習熟度」タブから登録してください。</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!faceDraft} title="業務・抽象度の設定" onClose={() => setFaceDraft(null)} size="sm"
        footer={faceDraft && (
          <>
            <Button variant="secondary" onClick={() => setFaceDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveFace || faceMut.isPending} loading={faceMut.isPending} onClick={() => faceMut.mutate(faceDraft)}>保存</Button>
          </>
        )}>
        {faceDraft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">業務</label>
              <Select value={faceDraft.businessFunctionId || '__none__'} onValueChange={(v) => setFaceDraft({ ...faceDraft, businessFunctionId: v === '__none__' ? '' : v })}>
                <SelectTrigger aria-label="業務"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">未分類 (設定しない)</SelectItem>
                  {businessFunctions.map((b) => <SelectItem key={b.business_function_id} value={b.business_function_id}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">抽象度</label>
              <Select value={faceDraft.abstractionLevel || '__none__'} onValueChange={(v) => setFaceDraft({ ...faceDraft, abstractionLevel: v === '__none__' ? '' : v })}>
                <SelectTrigger aria-label="抽象度"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">未設定</SelectItem>
                  {abstractionLevels.map((lv) => <SelectItem key={lv.code} value={lv.code}>{lv.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {abstractionLevels.length === 0 && (
                <p className="text-xs text-text-sub mt-1">抽象度がまだありません。「抽象度を管理」から先に登録してください。</p>
              )}
            </div>
            {!canSaveFace && (
              <p className="text-xs text-alert-text">業務と抽象度は両方設定するか、両方未設定にしてください。</p>
            )}
            <div>
              <label className="block text-sm text-text-sub mb-1">評価尺度 (任意)</label>
              <Select value={faceDraft.scaleId || '__none__'} onValueChange={(v) => setFaceDraft({ ...faceDraft, scaleId: v === '__none__' ? '' : v })}>
                <SelectTrigger aria-label="評価尺度"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">未設定</SelectItem>
                  {scales.map((sc) => <SelectItem key={sc.scale_id} value={sc.scale_id}>{sc.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-text-body min-h-[44px]">
              <input type="checkbox" checked={faceDraft.isEnabled} onChange={(e) => setFaceDraft({ ...faceDraft, isEnabled: e.target.checked })} className="w-4 h-4" />
              有効
            </label>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="スキルを削除" message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.skill_id)} />

      {/* D-720 (2026-09-06): 抽象度の管理 (評価尺度管理モーダルと同型のパターン)。codeは作成後不変。 */}
      <Modal open={abstractionLevelsModalOpen} title="抽象度を管理" onClose={() => { setAbstractionLevelsModalOpen(false); setAbstractionLevelForm(null); setMaxCountDraft(null); }} size="sm">
        <div className="space-y-3">
          {/* design-expert指摘: 現在数/上限を可視化し、上限到達時は追加操作を無効化する */}
          <p className="text-xs text-text-sub">現在 {abstractionLevels.length} / 最大 {abstractionLevelMaxCount} 件</p>
          {canManage && !abstractionLevelForm && (
            <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} disabled={abstractionLevelAtLimit}
              title={abstractionLevelAtLimit ? `最大保持数 (${abstractionLevelMaxCount}件) に達しています。追加するには下記の最大保持数を増やしてください。` : undefined}
              onClick={() => setAbstractionLevelForm({ id: null, code: '', label: '', sort_order: String((abstractionLevels.length + 1) * 10) })}>
              抽象度を追加
            </Button>
          )}
          {abstractionLevels.length === 0 ? (
            <p className="text-sm text-text-sub">抽象度がまだありません。「抽象度を追加」から登録してください。</p>
          ) : (
            <ul className="space-y-2">
              {[...abstractionLevels].sort((a, b) => a.sort_order - b.sort_order).map((lv) => (
                <li key={lv.level_id} className="border border-surface-border rounded-lg p-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-sm font-semibold text-text-body break-words">{lv.label}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{lv.code}</span>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button type="button" aria-label="編集" onClick={() => setAbstractionLevelForm({
                          id: lv.level_id, code: lv.code, label: lv.label, sort_order: String(lv.sort_order),
                        })} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                        <button type="button" aria-label="削除" onClick={() => setDeleteAbstractionLevelTarget(lv)}
                          className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* HIGH是正 (D-720): テナントごとの最大保持数を編集する。抽象度CRUDと同じ skill:manage
              (全店舗スコープ) が必要なため canManage で保護する (routes/tenant-settings.ts と対応)。 */}
          {canManage && (
            <div className="border-t border-surface-line pt-3">
              <label className="block text-sm text-text-sub mb-1" htmlFor="abstraction-level-max-count">最大保持数</label>
              <div className="flex items-center gap-2">
                <input id="abstraction-level-max-count" type="number" min={1} step={1}
                  value={maxCountDraftValue}
                  onChange={(e) => setMaxCountDraft(e.target.value)}
                  className={`${inputCls} w-24`} />
                <Button variant="secondary" size="sm" disabled={!canSaveMaxCount || maxCountSaveMut.isPending} loading={maxCountSaveMut.isPending}
                  onClick={() => maxCountSaveMut.mutate(Number(maxCountDraftValue))}>保存</Button>
              </div>
              {Number(maxCountDraftValue) < abstractionLevels.length && (
                <p className="text-xs text-danger-text mt-1">現在登録されている件数 ({abstractionLevels.length}件) を下回る値には設定できません。</p>
              )}
            </div>
          )}

          {abstractionLevelForm && (
            <div className="border-t border-surface-line pt-3 space-y-2">
              <div>
                <label className="block text-sm text-text-sub mb-1">コード</label>
                <input value={abstractionLevelForm.code} disabled={abstractionLevelForm.id !== null}
                  onChange={(e) => setAbstractionLevelForm({ ...abstractionLevelForm, code: e.target.value })} maxLength={50} className={inputCls} />
                {abstractionLevelForm.id !== null && <p className="text-xs text-text-sub mt-1">コードは作成後に変更できません。</p>}
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">名称</label>
                <input value={abstractionLevelForm.label} onChange={(e) => setAbstractionLevelForm({ ...abstractionLevelForm, label: e.target.value })} maxLength={100} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">並び順</label>
                <input type="number" value={abstractionLevelForm.sort_order} onChange={(e) => setAbstractionLevelForm({ ...abstractionLevelForm, sort_order: e.target.value })} className={inputCls} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setAbstractionLevelForm(null)}>キャンセル</Button>
                <Button variant="primary" size="sm" disabled={!canSaveAbstractionLevel || abstractionLevelSaveMut.isPending} loading={abstractionLevelSaveMut.isPending}
                  onClick={() => abstractionLevelForm && abstractionLevelSaveMut.mutate(abstractionLevelForm)}>保存</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* design-expert指摘: 参照件数 (使用中のスキル数) を事前提示する。0件なら通常の削除確認文言のまま。
          ⚠️ 参照が実在すれば DELETE 側 (assertNoAbstractionLevelReferences) が409で拒否するため、
          ここでの件数表示はあくまで事前の目安 (削除実行前に他ユーザーが割当を変える可能性はある)。 */}
      <ConfirmDialog open={!!deleteAbstractionLevelTarget} title="抽象度を削除"
        message={deleteAbstractionLevelTarget && abstractionLevelUsageCount(deleteAbstractionLevelTarget.code) > 0
          ? `「${deleteAbstractionLevelTarget.label}」は ${abstractionLevelUsageCount(deleteAbstractionLevelTarget.code)} 件のスキルで使用中のため削除できません。先に対象スキルの抽象度を変更してください。`
          : `「${deleteAbstractionLevelTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する"
        destructive loading={abstractionLevelDeleteMut.isPending}
        confirmDisabled={!!deleteAbstractionLevelTarget && abstractionLevelUsageCount(deleteAbstractionLevelTarget.code) > 0}
        onClose={() => setDeleteAbstractionLevelTarget(null)} onConfirm={() => deleteAbstractionLevelTarget && abstractionLevelDeleteMut.mutate(deleteAbstractionLevelTarget.level_id)} />
    </div>
  );
}

// =============================================================================
// 習熟度タブ (人・組織機能 増分② D-640): 評価尺度の管理 + 尺度ごとの段階編集
// =============================================================================
type SkillLevel = { skill_level_id: string; code: string; label: string; sort_order: number; scale_id: string | null; behavior_description: string | null };
type LevelDraft = { id: string | null; code: string; label: string; sort_order: string; behaviorDescription: string; effective_from: string };
type ScaleDraft = { id: string | null; code: string; label: string; effective_from: string };

const DEFAULT_SCALE = '__default__';

/** v0.5 マスタ変更履歴ビューア フェーズ1 §2-3 */
const SKILL_LEVEL_HISTORY_FIELD_LABELS: Record<string, string> = {
  code: 'コード', label: '名称', sort_order: '並び順', scale_id: '評価尺度ID', behavior_description: '行動記述',
};
const EVALUATION_SCALE_HISTORY_FIELD_LABELS: Record<string, string> = { code: 'コード', label: '名称', is_locked: 'ロック中' };

function SkillLevelsTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [selectedScale, setSelectedScale] = useState<string>(DEFAULT_SCALE);
  const [levelDraft, setLevelDraft] = useState<LevelDraft | null>(null);
  const [deleteLevelTarget, setDeleteLevelTarget] = useState<SkillLevel | null>(null);
  const [scalesModalOpen, setScalesModalOpen] = useState(false);
  const [scaleForm, setScaleForm] = useState<ScaleDraft | null>(null);

  const { data: scales = [] } = useQuery<EvaluationScale[]>({ queryKey: ['evaluation-scales'], queryFn: () => apiFetch('/masters/evaluation-scales') });
  const { data: levels = [], isLoading } = useQuery<SkillLevel[]>({ queryKey: ['skill-levels'], queryFn: () => apiFetch('/masters/skill-levels') });

  const currentScale = selectedScale === DEFAULT_SCALE ? null : scales.find((s) => s.scale_id === selectedScale) ?? null;
  const isLockedSelected = currentScale?.is_locked ?? false;
  const filteredLevels = levels
    .filter((l) => (selectedScale === DEFAULT_SCALE ? !l.scale_id : l.scale_id === selectedScale))
    .sort((a, b) => a.sort_order - b.sort_order);

  const levelSaveMut = useMutation({
    mutationFn: (d: LevelDraft) => {
      const payload: Record<string, unknown> = {
        code: d.code.trim(), label: d.label.trim(), sort_order: Number(d.sort_order) || 0,
        behavior_description: d.behaviorDescription.trim() || null,
      };
      if (!d.id) payload.scale_id = selectedScale === DEFAULT_SCALE ? null : selectedScale;
      if (d.effective_from) payload.effective_from = d.effective_from;
      return d.id
        ? apiFetch(`/masters/skill-levels/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/masters/skill-levels', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['skill-levels'] }); setLevelDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const levelDeleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/skill-levels/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['skill-levels'] }); setDeleteLevelTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteLevelTarget(null); },
  });

  const scaleSaveMut = useMutation({
    mutationFn: (d: ScaleDraft) => {
      const payload: Record<string, unknown> = { code: d.code.trim(), label: d.label.trim() };
      if (d.effective_from) payload.effective_from = d.effective_from;
      return d.id
        ? apiFetch<{ scale_id: string }>(`/masters/evaluation-scales/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch<{ scale_id: string }>('/masters/evaluation-scales', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: (res, d) => {
      qc.invalidateQueries({ queryKey: ['evaluation-scales'] });
      setScaleForm(null);
      if (!d.id) setSelectedScale(res.scale_id);
      toast.success('保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const scaleDeleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/evaluation-scales/${id}`, { method: 'DELETE' }),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: ['evaluation-scales'] });
      if (selectedScale === id) setSelectedScale(DEFAULT_SCALE);
      toast.success('削除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const canSaveLevel = !!levelDraft && levelDraft.code.trim() !== '' && levelDraft.label.trim() !== ''
    && (levelDraft.id !== null || levelDraft.behaviorDescription.trim() !== '');
  const scaleFormLocked = !!scaleForm?.id && scales.find((s) => s.scale_id === scaleForm.id)?.is_locked === true;
  const canSaveScale = !!scaleForm && scaleForm.code.trim() !== '' && scaleForm.label.trim() !== '' && !scaleFormLocked;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub">
          習熟度の段階を尺度ごとに登録します。並び順が下から上への習熟順になります (例: 見習い→一人前→指導可)。
        </p>
        <Button variant="secondary" size="sm" onClick={() => setScalesModalOpen(true)}>評価尺度を管理</Button>
      </div>

      <div className="max-w-xs">
        <label className="block text-xs text-text-sub mb-1">尺度</label>
        <Select value={selectedScale} onValueChange={setSelectedScale}>
          <SelectTrigger aria-label="尺度"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT_SCALE}>既定尺度</SelectItem>
            {scales.map((sc) => <SelectItem key={sc.scale_id} value={sc.scale_id}>{sc.label}{sc.is_locked ? ' (ロック中)' : ''}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLockedSelected && (
        <p className="text-xs text-alert-text bg-alert-bg border border-alert-border rounded px-3 py-2">
          この評価尺度には認定データが既に存在するため、段階を追加/削除できません。新しい尺度を作成してください。
        </p>
      )}

      {canManage && (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} disabled={isLockedSelected}
            onClick={() => setLevelDraft({ id: null, code: '', label: '', sort_order: String(filteredLevels.length), behaviorDescription: '', effective_from: '' })}>
            段階を追加
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : filteredLevels.length === 0 ? (
        <EmptyState message="この尺度の段階がまだありません" description={canManage && !isLockedSelected ? '「段階を追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {filteredLevels.map((l) => (
            <li key={l.skill_level_id} className="border border-surface-border rounded-lg bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-body break-words">{l.label}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{l.code}</span>
                  </div>
                  {l.behavior_description && <p className="text-xs text-text-sub mt-1 break-words">{l.behavior_description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <MasterHistoryPanel entityRoute="/masters/skill-levels" id={l.skill_level_id} label="習熟度" fieldLabels={SKILL_LEVEL_HISTORY_FIELD_LABELS} />
                  {canManage && (
                    <>
                      <button type="button" aria-label="編集" onClick={() => setLevelDraft({
                        id: l.skill_level_id, code: l.code, label: l.label, sort_order: String(l.sort_order), behaviorDescription: l.behavior_description ?? '', effective_from: '',
                      })} className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                      <button type="button" aria-label="削除" disabled={isLockedSelected} onClick={() => setDeleteLevelTarget(l)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!levelDraft} title={levelDraft?.id ? '段階を編集' : '段階を追加'} onClose={() => setLevelDraft(null)} size="sm"
        footer={levelDraft && (
          <>
            <Button variant="secondary" onClick={() => setLevelDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveLevel || levelSaveMut.isPending} loading={levelSaveMut.isPending} onClick={() => levelSaveMut.mutate(levelDraft)}>保存</Button>
          </>
        )}>
        {levelDraft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={levelDraft.code} onChange={(e) => setLevelDraft({ ...levelDraft, code: e.target.value })} maxLength={50} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={levelDraft.label} onChange={(e) => setLevelDraft({ ...levelDraft, label: e.target.value })} maxLength={100} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">並び順</label>
              <input type="number" value={levelDraft.sort_order} onChange={(e) => setLevelDraft({ ...levelDraft, sort_order: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">
                行動記述{levelDraft.id === null ? '' : ' (任意)'}
              </label>
              <textarea value={levelDraft.behaviorDescription} onChange={(e) => setLevelDraft({ ...levelDraft, behaviorDescription: e.target.value })}
                maxLength={500} rows={2} className={textareaCls} placeholder="この段階に達している人がどう振る舞うかを具体的に記述します" />
            </div>
            <EffectiveFromField value={levelDraft.effective_from} onChange={(v) => setLevelDraft({ ...levelDraft, effective_from: v })}
              error={getApiFieldError(levelSaveMut.error, 'effective_from')} />
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteLevelTarget} title="段階を削除" message={`「${deleteLevelTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={levelDeleteMut.isPending}
        onClose={() => setDeleteLevelTarget(null)} onConfirm={() => deleteLevelTarget && levelDeleteMut.mutate(deleteLevelTarget.skill_level_id)} />

      <Modal open={scalesModalOpen} title="評価尺度を管理" onClose={() => { setScalesModalOpen(false); setScaleForm(null); }} size="sm">
        <div className="space-y-3">
          {canManage && !scaleForm && (
            <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setScaleForm({ id: null, code: '', label: '', effective_from: '' })}>
              尺度を追加
            </Button>
          )}
          {scales.length === 0 ? (
            <p className="text-sm text-text-sub">評価尺度がまだありません。段階を「既定尺度」のまま運用する場合は作成不要です。</p>
          ) : (
            <ul className="space-y-2">
              {scales.map((sc) => (
                <li key={sc.scale_id} className="border border-surface-border rounded-lg p-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-sm font-semibold text-text-body break-words">{sc.label}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{sc.code}</span>
                      {sc.is_locked && <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">ロック中</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <MasterHistoryPanel entityRoute="/masters/evaluation-scales" id={sc.scale_id} label="評価尺度" fieldLabels={EVALUATION_SCALE_HISTORY_FIELD_LABELS} />
                      {canManage && (
                        <>
                          <button type="button" aria-label="編集" onClick={() => setScaleForm({ id: sc.scale_id, code: sc.code, label: sc.label, effective_from: '' })}
                            className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                          <button type="button" aria-label="削除" onClick={() => scaleDeleteMut.mutate(sc.scale_id)}
                            className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {scaleForm && (
            <div className="border-t border-surface-line pt-3 space-y-2">
              {scaleFormLocked && (
                <p className="text-xs text-alert-text">ロック中: 認定データが既に存在するため変更できません。</p>
              )}
              <div>
                <label className="block text-sm text-text-sub mb-1">コード</label>
                <input value={scaleForm.code} disabled={scaleFormLocked} onChange={(e) => setScaleForm({ ...scaleForm, code: e.target.value })} maxLength={50} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">名称</label>
                <input value={scaleForm.label} disabled={scaleFormLocked} onChange={(e) => setScaleForm({ ...scaleForm, label: e.target.value })} maxLength={100} className={inputCls} />
              </div>
              <EffectiveFromField value={scaleForm.effective_from} disabled={scaleFormLocked}
                onChange={(v) => setScaleForm({ ...scaleForm, effective_from: v })}
                error={getApiFieldError(scaleSaveMut.error, 'effective_from')} />
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setScaleForm(null)}>キャンセル</Button>
                <Button variant="primary" size="sm" disabled={!canSaveScale || scaleSaveMut.isPending} loading={scaleSaveMut.isPending}
                  onClick={() => scaleForm && scaleSaveMut.mutate(scaleForm)}>保存</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

// =============================================================================
// 人材像タブ (既存のロール要件CRUD + series列 + 前提関係セクション、人・組織機能 増分② E6/E8)
// =============================================================================
type RoleProfile = {
  role_profile_id: string; code: string; label: string; description: string | null; sort_order: number; series: string | null;
  skill_requirements?: { skill_id: string; min_skill_level_id: string }[];
  // v0.5 ② (D-766/D-778): 業務・スキルの親ノード (「くくり」) を必要スキルに指定する行。
  skill_targets?: { target_entity_type: 'business_function' | 'skill'; target_id: string; target_abstraction_level_code: string | null }[];
};
/** v0.5 マスタ変更履歴ビューア フェーズ1 §2-3。skill_requirements/skill_targets はバージョン管理を
 *  持たないため対象外 (バックエンド routes/skill-masters.ts の /role-profiles/:id/history と揃える)。 */
const ROLE_PROFILE_HISTORY_FIELD_LABELS: Record<string, string> = {
  code: 'コード', label: '名称', description: '説明', sort_order: '並び順', series: '系統',
};
type SkillRequirementDraft = { skill_id: string; min_skill_level_id: string };
type SkillTargetDraft = { target_entity_type: 'business_function' | 'skill'; target_id: string; target_abstraction_level_code: string | null };
type RoleProfileDraft = {
  id: string | null; code: string; label: string; description: string; sort_order: string; series: string;
  skill_requirements: SkillRequirementDraft[];
  skill_targets: SkillTargetDraft[];
  effective_from: string;
};

type PrerequisiteEntry = { prerequisite_role_profile_id: string; logic_type: 'and' | 'or'; group_no: number };

function RolePrerequisitesSection({ profiles, canManage }: { profiles: RoleProfile[]; canManage: boolean }) {
  const qc = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [entries, setEntries] = useState<PrerequisiteEntry[] | null>(null);

  const { data, isLoading } = useQuery<{ role_profile_id: string; prerequisites: PrerequisiteEntry[] }>({
    queryKey: ['role-prerequisites', selectedRoleId],
    queryFn: () => apiFetch(`/masters/role-profiles/${selectedRoleId}/prerequisites`),
    enabled: !!selectedRoleId,
  });

  useEffect(() => { setEntries(null); }, [selectedRoleId]);
  useEffect(() => { if (data) setEntries(data.prerequisites.map((p) => ({ ...p }))); }, [data]);

  const saveMut = useMutation({
    mutationFn: (list: PrerequisiteEntry[]) => apiFetch(`/masters/role-profiles/${selectedRoleId}/prerequisites`, {
      method: 'PUT', body: JSON.stringify({ prerequisites: list }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['role-prerequisites', selectedRoleId] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const otherProfiles = profiles.filter((p) => p.role_profile_id !== selectedRoleId);

  const updateEntry = (i: number, patch: Partial<PrerequisiteEntry>) => {
    if (!entries) return;
    setEntries(entries.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  };
  const removeEntry = (i: number) => { if (entries) setEntries(entries.filter((_, idx) => idx !== i)); };
  const addEntry = () => {
    if (!entries || otherProfiles.length === 0) return;
    const used = new Set(entries.map((e) => e.prerequisite_role_profile_id));
    const next = otherProfiles.find((p) => !used.has(p.role_profile_id)) ?? otherProfiles[0]!;
    setEntries([...entries, { prerequisite_role_profile_id: next.role_profile_id, logic_type: 'and', group_no: 0 }]);
  };

  return (
    <div className="border-t border-surface-line pt-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-text-body">前提関係</h3>
        <p className="text-xs text-text-sub mt-0.5">ある人材像を得るために先に必要な人材像を設定します (「なし」の場合は何も追加しません)。</p>
      </div>
      <div className="max-w-xs">
        <label className="block text-xs text-text-sub mb-1">対象の人材像</label>
        <Select value={selectedRoleId || '__none__'} onValueChange={(v) => setSelectedRoleId(v === '__none__' ? '' : v)}>
          <SelectTrigger aria-label="対象の人材像"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">選択してください</SelectItem>
            {profiles.map((p) => <SelectItem key={p.role_profile_id} value={p.role_profile_id}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {selectedRoleId && (
        isLoading || entries === null ? (
          <div className="text-sm text-text-sub">読み込み中…</div>
        ) : (
          <div className="space-y-2">
            {entries.length === 0 ? (
              <p className="text-xs text-text-sub">前提関係はありません。</p>
            ) : (
              <div className="space-y-2">
                {entries.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap border border-surface-line rounded p-2">
                    <div className="flex-1 min-w-[140px]">
                      <Select value={e.prerequisite_role_profile_id} onValueChange={(v) => updateEntry(i, { prerequisite_role_profile_id: v })} disabled={!canManage}>
                        <SelectTrigger aria-label="前提の人材像"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {otherProfiles.map((p) => <SelectItem key={p.role_profile_id} value={p.role_profile_id}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24 shrink-0">
                      <Select value={e.logic_type} onValueChange={(v) => updateEntry(i, { logic_type: v as 'and' | 'or' })} disabled={!canManage}>
                        <SelectTrigger aria-label="論理"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="and">AND</SelectItem>
                          <SelectItem value="or">OR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20 shrink-0">
                      <input type="number" value={e.group_no} disabled={!canManage}
                        onChange={(ev) => updateEntry(i, { group_no: Number(ev.target.value) || 0 })}
                        aria-label="グループ番号" className={inputCls} />
                    </div>
                    {canManage && (
                      <button type="button" aria-label="この前提を削除" onClick={() => removeEntry(i)}
                        className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {canManage && (
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addEntry} disabled={otherProfiles.length === 0}>前提を追加</Button>
                <Button variant="primary" size="sm" loading={saveMut.isPending} onClick={() => entries && saveMut.mutate(entries)}>前提関係を保存</Button>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

// =============================================================================
// 前提役割の上書きセクション (人・組織機能 増分② E9/D-629ゲート#4、D-677: 簡易版・自己承認)
// =============================================================================
type PrerequisiteOverride = {
  override_id: string; employee_id: string | null; external_person_id: string | null;
  role_profile_id: string; reason: string; approved_by: string | null; approved_at: string | null;
};
type TalentEmployeeOption = { employee_id: string; display_name: string };
type OverrideDraft = { employeeId: string; roleProfileId: string; reason: string };

function RolePrerequisiteOverridesSection({ profiles, canManage }: { profiles: RoleProfile[]; canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<OverrideDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PrerequisiteOverride | null>(null);

  // GET /masters/role-prerequisite-overrides は requireTenantWideManage (skill:manage:all_stores) を
  // 要求するため canManage=false では確実に403になる (QA/platform-reviewerレビュー指摘: 未ガードだと
  // 「上書きはありません」と誤読されるため enabled で止める)。
  const { data: overrides = [], isLoading, isError: overridesError } = useQuery<PrerequisiteOverride[]>({
    queryKey: ['role-prerequisite-overrides'], queryFn: () => apiFetch('/masters/role-prerequisite-overrides'),
    enabled: canManage, retry: false,
  });
  // ⚠️ D-629ゲート#4の簡易版UI: 対象者は社員のみ扱う (外部人材は対象外)。TalentCertificationsPage の
  // インポートUIと同じ簡略化 (API 自体は external_person_id にも対応するが、画面は増やさない §6d)。
  // 一覧は `/talent/skills` (skill:view の配下メンバー母集団) から取得する。この上書き作成に必要な
  // 権限 (skill:manage:all_stores) は `/employees` が要求する isCompanyAdmin とはゲートが異なるため、
  // 権限モデルが一致する `/talent/skills` を使う。
  // ⚠️ platform-reviewer指摘: skill:manage:all_stores を持つが skill:view:all_stores を持たないカスタム
  // ロールでは、このクエリが403になり対象者を1人も選べない (「作れるが誰も選べない」非対称)。
  // 増分②では権限モデルの再設計まではせず、isError を表示して原因を伝えるに留める。
  const { data: talentData, isError: talentError } = useQuery<{ employees: TalentEmployeeOption[] }>({
    queryKey: ['talent-skills-for-overrides'], queryFn: () => apiFetch('/talent/skills'), enabled: canManage, retry: false,
  });
  const employees = talentData?.employees ?? [];
  const employeeLabel = (id: string | null) => (id ? employees.find((e) => e.employee_id === id)?.display_name ?? id : null);
  const roleLabel = (id: string) => profiles.find((p) => p.role_profile_id === id)?.label ?? id;

  const createMut = useMutation({
    mutationFn: (d: OverrideDraft) => apiFetch('/masters/role-prerequisite-overrides', {
      method: 'POST', body: JSON.stringify({ employee_id: d.employeeId, role_profile_id: d.roleProfileId, reason: d.reason.trim() }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['role-prerequisite-overrides'] }); setDraft(null); toast.success('登録しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '登録に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/role-prerequisite-overrides/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['role-prerequisite-overrides'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.employeeId !== '' && draft.roleProfileId !== '' && draft.reason.trim() !== '';

  return (
    <div className="border-t border-surface-line pt-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold text-text-body">前提役割の上書き</h3>
          <p className="text-xs text-text-sub mt-0.5">
            前提関係を満たしていない対象者に、理由を残した上で例外的に人材像を認める記録です。
            登録すると登録者自身の承認として即時記録されます (簡易運用)。
          </p>
        </div>
        {canManage && employees.length > 0 && profiles.length > 0 && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({ employeeId: employees[0]!.employee_id, roleProfileId: profiles[0]!.role_profile_id, reason: '' })}>
            上書きを追加
          </Button>
        )}
      </div>

      {canManage && talentError && (
        <p className="text-xs text-alert-text">対象者の一覧を取得できませんでした (配下メンバーの閲覧権限が必要です)。</p>
      )}

      {!canManage ? (
        <p className="text-xs text-text-sub">前提役割の上書きの閲覧には全店舗管理権限が必要です。</p>
      ) : isLoading ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : overridesError ? (
        <p role="alert" className="text-xs text-alert-text">前提役割の上書きの取得に失敗しました。</p>
      ) : overrides.length === 0 ? (
        <p className="text-xs text-text-sub">前提役割の上書きはありません。</p>
      ) : (
        <ul className="space-y-2">
          {overrides.map((o) => (
            <li key={o.override_id} className="border border-surface-border rounded-lg bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-body break-words">{employeeLabel(o.employee_id) ?? o.external_person_id}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap">{roleLabel(o.role_profile_id)}</span>
                  </div>
                  <p className="text-xs text-text-sub mt-1 break-words">{o.reason}</p>
                </div>
                {canManage && (
                  <button type="button" aria-label="この上書きを削除" onClick={() => setDeleteTarget(o)}
                    className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title="前提役割の上書きを追加" onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || createMut.isPending} loading={createMut.isPending} onClick={() => createMut.mutate(draft)}>登録</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">対象者</label>
              <Select value={draft.employeeId} onValueChange={(v) => setDraft({ ...draft, employeeId: v })}>
                <SelectTrigger aria-label="対象者"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">人材像</label>
              <Select value={draft.roleProfileId} onValueChange={(v) => setDraft({ ...draft, roleProfileId: v })}>
                <SelectTrigger aria-label="人材像"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => <SelectItem key={p.role_profile_id} value={p.role_profile_id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">理由 (必須)</label>
              <textarea value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} maxLength={500} rows={3}
                placeholder="例: 前提の人材像は未取得だが、実務経験を考慮し例外的に認める" className={textareaCls} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="前提役割の上書きを削除"
        message={`「${employeeLabel(deleteTarget?.employee_id ?? null) ?? deleteTarget?.external_person_id}」の上書きを削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.override_id)} />
    </div>
  );
}

/** v0.5 ② (D-761/§5-6): taxonomy nodes (id/label/parent_id のフラット配列) から
 *  「葉名 ← 祖先1 › 祖先2」の1行ラベルを組み立てる。shadcn Select の ItemText は末尾を truncate する
 *  ため (`select.tsx:44/136`)、深さ6のパンくずでも最も識別力のある葉が必ず見える形にする。 */
const buildLeafFirstLabel = (nodes: TaxonomyNode[], id: string): string => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const target = byId.get(id);
  if (!target) return id;
  const ancestors: string[] = [];
  let cur = target.parent_id ? byId.get(target.parent_id) : undefined;
  while (cur) { ancestors.push(cur.label); cur = cur.parent_id ? byId.get(cur.parent_id) : undefined; }
  return ancestors.length > 0 ? `${target.label} ← ${ancestors.join(' › ')}` : target.label;
};

type TaxonomyNode = { id: string; code: string; label: string; parent_id: string | null };
type TaxonomyResponse = { entity_type: string; nodes: TaxonomyNode[] };
type ExpansionEntry = {
  target_id: string; target_abstraction_level_code: string | null; expanded_face_count: number;
  // ⚠️ 実測 (apps/api/src/lib/people-org/required-units.ts の GroupEmptyReason): この API が
  // 返しうる理由は 'no_faces' (配下に面が無い/配下のスキルが未分類、区別しない) と
  // 'expansion_disabled' (階層の展開が無効) の2つのみ。'fully_covered_by_explicit' は
  // /competency/coverage 側の概念でありこの展開プレビューAPIからは返らない。
  reason: 'no_faces' | 'expansion_disabled' | 'fully_covered_by_explicit' | null;
  pruned_required_branches: number; undeterminable_face_count: number;
};
type HierarchySetting = { entity_type: string; pay_increment_includes_group_targets: boolean };
const EXPANSION_REASON_LABELS: Record<string, string> = {
  no_faces: '配下に面 (スキル) がありません (未分類のスキルのみの場合も含みます)',
  expansion_disabled: '階層の展開が無効になっています',
  fully_covered_by_explicit: '個別に指定した必要スキルで既に充足しています',
};

function RoleProfilesTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<RoleProfileDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoleProfile | null>(null);

  const { data: skills = [] } = useQuery<{ skill_id: string; label: string }[]>({ queryKey: ['skills'], queryFn: () => apiFetch('/masters/skills') });
  // v0.4 追加要件⑬ (D-395/D-410 C-3): 習熟度が実質未整備のテナントでは最低習熟度の選択を隠し
  // 唯一の習熟度を自動補完する (D-64 人材像→スキル自動展開が簡易運用でも使えるようにする)。
  const { skillLevels, isSimplified: skillLevelSimplified, defaultLevelId } = useSkillLevelSimplified();

  // v0.5 ② (D-761/§5-4): 「くくり」(業務・スキルの親ノード) 選択・展開後の面数プレビュー用。
  const { data: bfTaxonomy } = useQuery<TaxonomyResponse>({ queryKey: ['competency-taxonomy', 'business_function'], queryFn: () => apiFetch('/masters/taxonomy/business_function') });
  const { data: skillTaxonomy } = useQuery<TaxonomyResponse>({ queryKey: ['competency-taxonomy', 'skill'], queryFn: () => apiFetch('/masters/taxonomy/skill') });
  const { data: abstractionLevels = [] } = useQuery<{ code: string; label: string }[]>({ queryKey: ['abstraction-levels'], queryFn: () => apiFetch('/masters/abstraction-levels') });
  const { data: hierarchySettingsData } = useQuery<{ settings: HierarchySetting[] }>({ queryKey: ['competency-hierarchy-settings'], queryFn: () => apiFetch('/masters/hierarchy-settings') });
  const payIncludesGroupTargets = (et: 'business_function' | 'skill') =>
    hierarchySettingsData?.settings.find((s) => s.entity_type === et)?.pay_increment_includes_group_targets ?? false;

  const { data, isLoading } = useQuery<RoleProfile[]>({ queryKey: ['role-profiles'], queryFn: () => apiFetch('/masters/role-profiles') });
  const profiles = data ?? [];

  const openEdit = async (p: RoleProfile) => {
    const detail = await apiFetch<RoleProfile>(`/masters/role-profiles/${p.role_profile_id}`);
    setDraft({
      id: p.role_profile_id, code: p.code, label: p.label, description: p.description ?? '', sort_order: String(p.sort_order),
      series: detail.series ?? '',
      skill_requirements: (detail.skill_requirements ?? []).map((r) => ({ ...r })),
      skill_targets: (detail.skill_targets ?? []).map((t) => ({ ...t })),
      effective_from: '',
    });
  };

  const saveMut = useMutation({
    mutationFn: (d: RoleProfileDraft) => {
      const payload: Record<string, unknown> = {
        code: d.code.trim(), label: d.label.trim(), description: d.description.trim() || null,
        sort_order: Number(d.sort_order) || 0,
        series: d.series.trim() || null,
        skill_requirements: d.skill_requirements.filter((r) => r.skill_id && r.min_skill_level_id),
        skill_targets: d.skill_targets.filter((t) => t.target_id),
      };
      if (d.effective_from) payload.effective_from = d.effective_from;
      return d.id
        ? apiFetch(`/masters/role-profiles/${d.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/masters/role-profiles', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['role-profiles'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/role-profiles/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['role-profiles'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  const updateReq = (i: number, patch: Partial<SkillRequirementDraft>) => {
    if (!draft) return;
    setDraft({ ...draft, skill_requirements: draft.skill_requirements.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  };
  const removeReq = (i: number) => { if (draft) setDraft({ ...draft, skill_requirements: draft.skill_requirements.filter((_, idx) => idx !== i) }); };
  const addReq = () => {
    if (!draft || skills.length === 0 || skillLevels.length === 0) return;
    setDraft({ ...draft, skill_requirements: [...draft.skill_requirements, { skill_id: skills[0]!.skill_id, min_skill_level_id: defaultLevelId ?? skillLevels[0]!.skill_level_id }] });
  };

  const updateTarget = (i: number, patch: Partial<SkillTargetDraft>) => {
    if (!draft) return;
    setDraft({ ...draft, skill_targets: draft.skill_targets.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) });
  };
  const removeTarget = (i: number) => { if (draft) setDraft({ ...draft, skill_targets: draft.skill_targets.filter((_, idx) => idx !== i) }); };
  const addTarget = () => {
    if (!draft) return;
    const bfNodes = bfTaxonomy?.nodes ?? [];
    const skillNodes = skillTaxonomy?.nodes ?? [];
    const entityType: 'business_function' | 'skill' = bfNodes.length > 0 ? 'business_function' : 'skill';
    const firstId = entityType === 'business_function' ? bfNodes[0]?.id : skillNodes[0]?.id;
    if (!firstId) return;
    setDraft({ ...draft, skill_targets: [...draft.skill_targets, { target_entity_type: entityType, target_id: firstId, target_abstraction_level_code: null }] });
  };

  // ⚠️ N+1 を避けるため、target_entity_type ごとに1回のリクエストへまとめる (§5-4)。
  const bfTargets = draft?.skill_targets.filter((t) => t.target_entity_type === 'business_function' && t.target_id) ?? [];
  const skillTargets = draft?.skill_targets.filter((t) => t.target_entity_type === 'skill' && t.target_id) ?? [];
  const bfTargetsParam = bfTargets.map((t) => `${t.target_id}:${t.target_abstraction_level_code ?? ''}`).join(',');
  const skillTargetsParam = skillTargets.map((t) => `${t.target_id}:${t.target_abstraction_level_code ?? ''}`).join(',');
  const { data: bfExpansion } = useQuery<{ targets: ExpansionEntry[] }>({
    queryKey: ['competency-expansion', 'business_function', bfTargetsParam],
    queryFn: () => apiFetch(`/masters/taxonomy/business_function/expansion?targets=${encodeURIComponent(bfTargetsParam)}`),
    enabled: bfTargets.length > 0,
  });
  const { data: skillExpansion } = useQuery<{ targets: ExpansionEntry[] }>({
    queryKey: ['competency-expansion', 'skill', skillTargetsParam],
    queryFn: () => apiFetch(`/masters/taxonomy/skill/expansion?targets=${encodeURIComponent(skillTargetsParam)}`),
    enabled: skillTargets.length > 0,
  });
  const findExpansion = (t: SkillTargetDraft): ExpansionEntry | undefined => {
    const pool = t.target_entity_type === 'business_function' ? bfExpansion?.targets : skillExpansion?.targets;
    return pool?.find((e) => e.target_id === t.target_id && (e.target_abstraction_level_code ?? null) === (t.target_abstraction_level_code ?? null));
  };
  const anyPayInfluenced = (draft?.skill_targets ?? []).some((t) => t.target_id && payIncludesGroupTargets(t.target_entity_type));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub">
          人材像は「(スキル, 最低習熟度) の集合」の名前付き短縮形です。1人が複数の人材像を兼務できます (例: 店長かつ麺場)。
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {/* v0.5 人材育成ダッシュボード: 旧 /talent/competency-matrix はタブ統合された
              (docs/design/v05-talent-development-dashboard-detail.md §0-2) */}
          <Link to="/talent/dashboard?tab=matrix" className="inline-flex items-center gap-1.5 text-sm text-misespark-navy underline underline-offset-2 min-h-[44px]">
            <Grid3x3 size={14} aria-hidden />業務×抽象度のマトリクスで見る
          </Link>
          {canManage && (
            <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
              onClick={() => setDraft({ id: null, code: '', label: '', description: '', sort_order: String(profiles.length), series: '', skill_requirements: [], skill_targets: [], effective_from: '' })}>
              人材像を追加
            </Button>
          )}
        </div>
      </div>
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : profiles.length === 0 ? (
        <EmptyState message="人材像がまだありません" description={canManage ? '「人材像を追加」から登録してください。先にスキル・習熟度タブでスキルを登録しておくと選びやすくなります。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {profiles.map((p) => (
            <li key={p.role_profile_id} className="flex items-start justify-between gap-2 border border-surface-border rounded-lg bg-white p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-text-body break-words">{p.label}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{p.code}</span>
                  {p.series && <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap">{p.series}</span>}
                </div>
                {p.description && <p className="text-xs text-text-sub mt-1 break-words">{p.description}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <MasterHistoryPanel entityRoute="/masters/role-profiles" id={p.role_profile_id} label="人材像" fieldLabels={ROLE_PROFILE_HISTORY_FIELD_LABELS} />
                {canManage && (
                  <>
                    <button type="button" aria-label="編集" onClick={() => openEdit(p)}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                    <button type="button" aria-label="削除" onClick={() => setDeleteTarget(p)}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <RolePrerequisitesSection profiles={profiles} canManage={canManage} />
      <RolePrerequisiteOverridesSection profiles={profiles} canManage={canManage} />

      <Modal open={!!draft} title={draft?.id ? '人材像を編集' : '人材像を追加'} onClose={() => setDraft(null)} size="md"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-text-sub mb-1">コード</label>
                <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">名称</label>
                <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={100} className={inputCls} placeholder="例: 麺場" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">系統 (任意、例: マネジメント系/技能系)</label>
              <input value={draft.series} onChange={(e) => setDraft({ ...draft, series: e.target.value })} maxLength={100} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">説明 (任意)</label>
              <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={500} rows={2} className={textareaCls} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm text-text-sub">必要なスキル・習熟度</label>
                <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addReq} disabled={skills.length === 0 || skillLevels.length === 0}>追加</Button>
              </div>
              {draft.skill_requirements.length === 0 ? (
                <p className="text-xs text-text-sub">スキル要件が未設定です (このままでも保存できますが、必要人員の充足判定で使えません)。</p>
              ) : (
                <div className="space-y-2">
                  {draft.skill_requirements.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap border border-surface-line rounded p-2">
                      <div className="flex-1 min-w-[140px]">
                        <Select value={r.skill_id} onValueChange={(v) => updateReq(i, { skill_id: v })}>
                          <SelectTrigger aria-label="スキル"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {skills.map((s) => <SelectItem key={s.skill_id} value={s.skill_id}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* v0.4 追加要件⑬ (D-395/D-410 C-3): 習熟度が実質1段階のみなら選択自体を出さない */}
                      {!skillLevelSimplified && (
                        <>
                          <span className="text-xs text-text-sub shrink-0">を最低</span>
                          <div className="flex-1 min-w-[120px]">
                            <Select value={r.min_skill_level_id} onValueChange={(v) => updateReq(i, { min_skill_level_id: v })}>
                              <SelectTrigger aria-label="最低習熟度"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {skillLevels.map((l) => <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <span className="text-xs text-text-sub shrink-0">以上</span>
                        </>
                      )}
                      <button type="button" aria-label="この要件を削除" onClick={() => removeReq(i)}
                        className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* v0.5 ② (D-761/D-766/§5-4): 業務・スキルの親ノード (「くくり」) 指定。
                展開により配下の面 (スキル) すべてが必要スキルとして扱われる。設定しただけでは
                何も起きないことがあるため「展開後の面：N件」を常時表示し、N=0のときは理由を出す。 */}
            <div className="space-y-2 border-t border-surface-line pt-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm text-text-sub">必要な業務・スキルの分類 (くくり)</label>
                <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addTarget}
                  disabled={(bfTaxonomy?.nodes ?? []).length === 0 && (skillTaxonomy?.nodes ?? []).length === 0}>追加</Button>
              </div>
              <p className="text-xs text-text-sub">
                個別のスキルではなく「業務の分類」や「スキルの分類」をまとめて必要スキルに指定します。分類配下の面 (スキル) がすべて対象になります。
              </p>
              {anyPayInfluenced && (
                <div className="flex items-start gap-2 p-2 rounded bg-alert-bg border border-alert-border/40">
                  <Info size={14} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
                  <p className="text-xs text-alert-text">この人材像の変更は給与加算に影響します。</p>
                </div>
              )}
              {draft.skill_targets.length === 0 ? (
                <p className="text-xs text-text-sub">分類の指定はありません。</p>
              ) : (
                <div className="space-y-2">
                  {draft.skill_targets.map((t, i) => {
                    const nodes = t.target_entity_type === 'business_function' ? (bfTaxonomy?.nodes ?? []) : (skillTaxonomy?.nodes ?? []);
                    const expansion = t.target_id ? findExpansion(t) : undefined;
                    return (
                      <div key={i} className="flex items-center gap-2 flex-wrap border border-surface-line rounded p-2">
                        <div className="w-28 shrink-0">
                          <Select value={t.target_entity_type} onValueChange={(v) => {
                            const nextType = v as 'business_function' | 'skill';
                            const nextNodes = nextType === 'business_function' ? (bfTaxonomy?.nodes ?? []) : (skillTaxonomy?.nodes ?? []);
                            updateTarget(i, { target_entity_type: nextType, target_id: nextNodes[0]?.id ?? '', target_abstraction_level_code: null });
                          }}>
                            <SelectTrigger aria-label="対象種別"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="business_function">業務</SelectItem>
                              <SelectItem value="skill">スキル</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1 min-w-[160px]">
                          <Select value={t.target_id} onValueChange={(v) => updateTarget(i, { target_id: v })}>
                            <SelectTrigger aria-label="対象ノード"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {nodes.map((n) => <SelectItem key={n.id} value={n.id}>{buildLeafFirstLabel(nodes, n.id)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-36 shrink-0">
                          <Select value={t.target_abstraction_level_code ?? '__all__'} onValueChange={(v) => updateTarget(i, { target_abstraction_level_code: v === '__all__' ? null : v })}>
                            <SelectTrigger aria-label="抽象度で絞り込み"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__all__">(すべて)</SelectItem>
                              {abstractionLevels.map((a) => <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap ${expansion && expansion.expanded_face_count === 0 ? 'bg-alert-bg text-alert-text border border-alert-border/40' : 'bg-surface-sunken text-text-sub border border-surface-border'}`}>
                          {expansion ? `展開後の面：${expansion.expanded_face_count}件` : '展開後の面：確認中…'}
                        </span>
                        <button type="button" aria-label="この分類指定を削除" onClick={() => removeTarget(i)}
                          className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                        {expansion && expansion.expanded_face_count === 0 && expansion.reason && (
                          <p className="text-xs text-alert-text w-full">{EXPANSION_REASON_LABELS[expansion.reason] ?? expansion.reason}</p>
                        )}
                        {expansion && expansion.pruned_required_branches > 0 && (
                          <p className="text-xs text-alert-text w-full">必須指定の枝が {expansion.pruned_required_branches} 件、面を持たないため対象から外れています。</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-surface-line pt-4">
              <EffectiveFromField value={draft.effective_from} onChange={(v) => setDraft({ ...draft, effective_from: v })}
                error={getApiFieldError(saveMut.error, 'effective_from')} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="人材像を削除" message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.role_profile_id)} />
    </div>
  );
}

// =============================================================================
// Excelアップロード一括登録タブ (v0.5、設計: docs/design/v05-competency-master-excel-import-detail.md)
// 既存の ImportsPage.tsx (CSV取込: アップロード→エラー一覧→確定 の2段階UX) と同型のパターンを踏襲。
// =============================================================================
type ExcelImportRowIssue = { sheet: string; row: number; column?: string; message: string };
type ExcelPlanSummaryEntry = { create: number; update?: number; skip?: number; existing?: number };
type ExcelPlanSummary = {
  business_functions: ExcelPlanSummaryEntry;
  evaluation_scales: ExcelPlanSummaryEntry;
  skill_levels: ExcelPlanSummaryEntry;
  skills: ExcelPlanSummaryEntry;
  role_profiles: ExcelPlanSummaryEntry;
};
type ExcelImportPreview = {
  job_id: string | null;
  can_commit: boolean;
  counts: { scale_level_rows: number; skill_rows: number; role_profile_rows: number };
  errors: ExcelImportRowIssue[];
  warnings: ExcelImportRowIssue[];
  plan_summary: ExcelPlanSummary;
};
type ExcelImportStageResult = { created: number; updated?: number; skipped?: number };
type ExcelImportCommitResult = {
  job_id: string;
  business_functions: ExcelImportStageResult;
  evaluation_scales: ExcelImportStageResult;
  skill_levels: ExcelImportStageResult;
  skills: ExcelImportStageResult;
  role_profiles: ExcelImportStageResult;
  failed_at: { stage: string; message: string } | null;
};

const EXCEL_ENTITY_LABELS: Record<keyof ExcelPlanSummary, string> = {
  business_functions: '業務', evaluation_scales: '評価尺度', skill_levels: '習熟度',
  skills: 'スキル', role_profiles: '人材像',
};

const downloadExcelImportTemplate = async () => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}/masters/skill-masters/excel-import/template`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  handleUnauthorized(res.status);
  if (!res.ok) { toast.error('雛形のダウンロードに失敗しました'); return; }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'skill-master-import-template.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function ExcelImportTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ExcelImportPreview | null>(null);
  const [commitResult, setCommitResult] = useState<ExcelImportCommitResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const uploadMut = useMutation({
    mutationFn: async (): Promise<ExcelImportPreview> => {
      if (!file) throw new Error('ファイルを選択してください');
      const form = new FormData();
      form.append('file', file);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE}/masters/skill-masters/excel-import`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      handleUnauthorized(res.status);
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'エラー');
      }
      return body.data;
    },
    onSuccess: (data) => { setPreview(data); setCommitResult(null); setMessage(null); },
    onError: (e) => { setMessage(e instanceof ApiClientError ? e.message : '検証に失敗しました'); setPreview(null); },
  });

  const invalidateAllMasters = () => {
    qc.invalidateQueries({ queryKey: ['business-functions'] });
    qc.invalidateQueries({ queryKey: ['evaluation-scales'] });
    qc.invalidateQueries({ queryKey: ['skill-levels'] });
    qc.invalidateQueries({ queryKey: ['skills'] });
    qc.invalidateQueries({ queryKey: ['role-profiles'] });
  };

  const commitMut = useMutation({
    mutationFn: () => apiFetch<ExcelImportCommitResult>(`/masters/skill-masters/excel-import/${preview!.job_id}/commit`, { method: 'POST' }),
    onSuccess: (data) => {
      setCommitResult(data);
      setPreview(null);
      setFile(null);
      setMessage(data.failed_at
        ? `⚠ 「${EXCEL_ENTITY_LABELS[data.failed_at.stage as keyof ExcelPlanSummary] ?? data.failed_at.stage}」の処理中に失敗しました。それより前の項目は反映済みです`
        : '✅ 取込を完了しました');
      invalidateAllMasters();
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確定に失敗しました'),
  });

  if (!canManage) {
    return <p className="text-sm text-text-sub">Excelアップロード一括登録の実行には全店舗管理権限が必要です。</p>;
  }

  const renderPlanTable = (rows: Array<{ key: keyof ExcelPlanSummary; created: number; updated?: number; skipped?: number }>) => (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-surface-sunken">
            <th className="text-left px-2 py-1.5 border-b border-surface-border">対象</th>
            <th className="text-right px-2 py-1.5 border-b border-surface-border">新規</th>
            <th className="text-right px-2 py-1.5 border-b border-surface-border">更新</th>
            <th className="text-right px-2 py-1.5 border-b border-surface-border">変更なし/既存</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-surface-line">
              <td className="px-2 py-1.5">{EXCEL_ENTITY_LABELS[r.key]}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.created}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.updated ?? '-'}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{r.skipped ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <p className="text-sm text-text-sub">
          評価尺度・習熟度／スキル／人材像をExcelでまとめて登録・更新します。まず雛形をダウンロードして記入し、アップロードしてください。
        </p>
        <Button variant="secondary" size="sm" leftIcon={<Download size={14} />} onClick={() => { void downloadExcelImportTemplate(); }}>
          雛形Excelをダウンロード
        </Button>
      </div>

      <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <div className="border-2 border-dashed border-surface-border rounded-lg p-6 text-center">
          <Upload size={28} className="mx-auto mb-2 text-text-sub" aria-hidden />
          {/* 実装後レビュー指摘 (design-expert CRITICAL): 未装飾のネイティブ input[type=file] は
              無条件不合格条件(タップ領域不足)に抵触するため、既存FilePickerField(sr-only input +
              44px以上のButton)へ置き換える */}
          <div className="flex justify-center">
            <FilePickerField
              label="登録するExcelファイルを選択"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={!!preview}
              onChange={(f) => { setFile(f); setPreview(null); setCommitResult(null); setMessage(null); }}
            />
          </div>
        </div>
        {!preview && (
          <div className="flex justify-end">
            <Button variant="primary" size="sm" disabled={!file || uploadMut.isPending} loading={uploadMut.isPending} onClick={() => uploadMut.mutate()}>
              アップロード＆検証
            </Button>
          </div>
        )}
      </div>

      {message && (
        <div role="status" aria-live="polite" className={`p-3 rounded border text-sm ${message.startsWith('✅') ? 'bg-success-bg text-success-text border-success-border' : 'bg-alert-bg text-alert-text border-alert-border'}`}>
          {message}
        </div>
      )}

      {preview && (
        <div className="border border-surface-border rounded-lg bg-white p-4 space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center text-xs">
            <div className="p-2 rounded bg-surface-sunken"><div className="text-text-sub">評価尺度・習熟度</div><div className="text-lg tabular-nums">{preview.counts.scale_level_rows}</div></div>
            <div className="p-2 rounded bg-surface-sunken"><div className="text-text-sub">スキル</div><div className="text-lg tabular-nums">{preview.counts.skill_rows}</div></div>
            <div className="p-2 rounded bg-surface-sunken"><div className="text-text-sub">人材像</div><div className="text-lg tabular-nums">{preview.counts.role_profile_rows}</div></div>
          </div>

          {renderPlanTable((Object.keys(preview.plan_summary) as Array<keyof ExcelPlanSummary>).map((key) => ({
            key, created: preview.plan_summary[key].create, updated: preview.plan_summary[key].update, skipped: preview.plan_summary[key].skip ?? preview.plan_summary[key].existing,
          })))}

          {/* 実装後レビュー指摘 (design-expert HIGH): 確定をブロックするエラーの方が警告より視覚的に
              弱かった (エラー側だけ色付きボックス・アイコンが無い)。色のみに頼らず色+アイコン+テキストの
              3重で伝え、エラーを警告より強い見た目にする */}
          {preview.warnings.length > 0 && (
            <div className="p-3 rounded bg-alert-bg border border-alert-border text-xs text-alert-text space-y-1">
              <div className="font-medium flex items-center gap-1.5">
                <AlertTriangle size={14} aria-hidden />
                警告 ({preview.warnings.length}件、確定は可能です)
              </div>
              <ul className="list-disc pl-5 space-y-0.5">
                {preview.warnings.map((w, i) => (
                  <li key={i}>[{w.sheet} {w.row}行目{w.column ? ` / ${w.column}` : ''}] {w.message}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.errors.length > 0 && (
            <div className="p-3 rounded bg-danger-bg border border-danger-border">
              <h3 className="text-sm font-semibold text-danger-text mb-1.5 flex items-center gap-1.5">
                <XCircle size={16} aria-hidden />
                エラー一覧 ({preview.errors.length}件)
              </h3>
              <div className="border border-surface-border rounded max-h-60 overflow-auto bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-surface-sunken text-text-sub sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">シート</th>
                      <th className="text-left px-2 py-1.5 w-14">行</th>
                      <th className="text-left px-2 py-1.5">列</th>
                      <th className="text-left px-2 py-1.5">内容</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((e, i) => (
                      <tr key={i} className="border-t border-surface-line">
                        <td className="px-2 py-1 whitespace-nowrap">{e.sheet}</td>
                        <td className="px-2 py-1 tabular-nums">{e.row || '-'}</td>
                        <td className="px-2 py-1 font-mono whitespace-nowrap">{e.column ?? '-'}</td>
                        <td className="px-2 py-1 text-danger-text">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-danger-text mt-1.5">エラーが残っている間は確定できません。Excelを修正して再度アップロードしてください。</p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setPreview(null); setFile(null); }}>キャンセル</Button>
            <Button variant="primary" size="sm" disabled={!preview.can_commit || !preview.job_id || commitMut.isPending} loading={commitMut.isPending}
              onClick={() => commitMut.mutate()}>
              確定
            </Button>
          </div>
        </div>
      )}

      {commitResult && (
        <div className="border border-surface-border rounded-lg bg-white p-4 space-y-2">
          <h3 className="text-sm font-semibold text-text-body">取込結果</h3>
          {renderPlanTable((['business_functions', 'evaluation_scales', 'skill_levels', 'skills', 'role_profiles'] as const).map((key) => ({
            key, created: commitResult[key].created, updated: commitResult[key].updated, skipped: commitResult[key].skipped,
          })))}
          {commitResult.failed_at && (
            <p className="text-xs text-danger-text">
              「{EXCEL_ENTITY_LABELS[commitResult.failed_at.stage as keyof ExcelPlanSummary] ?? commitResult.failed_at.stage}」の処理中に失敗しました: {commitResult.failed_at.message}
              <br />
              上の表の件数まではデータベースに反映済みです。原因を修正した上で、同じExcelを再度アップロードしてください (変更のない項目は自動的にスキップされます)。
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function SkillMastersPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'skill', 'manage');
  const [tab, setTab] = useState<'business-functions' | 'skills' | 'skill-levels' | 'role-profiles' | 'position-levels' | 'excel-import'>('business-functions');

  const tabs: ReadonlyArray<readonly [typeof tab, string]> = [
    ['business-functions', '業務'],
    ['skills', 'スキル（業務×抽象度）'],
    ['skill-levels', '習熟度'],
    ['role-profiles', '人材像'],
    ['position-levels', '役職・等級'],
    ['excel-import', 'Excel一括登録'],
  ] as const;

  const tabBody: Record<typeof tab, ReactNode> = {
    'business-functions': <SimpleMasterTab def={SIMPLE_MASTER_DEFS[0]!} canManage={canManage} />,
    skills: <SkillsFaceTab canManage={canManage} />,
    'skill-levels': <SkillLevelsTab canManage={canManage} />,
    'role-profiles': <RoleProfilesTab canManage={canManage} />,
    'position-levels': <SimpleMasterTab def={SIMPLE_MASTER_DEFS[1]!} canManage={canManage} />,
    'excel-import': <ExcelImportTab canManage={canManage} />,
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">スキル・人材像・役職</h2>
        <p className="text-sm text-text-sub mt-1">
          業務・スキル (業務×抽象度)・習熟度・人材像・役職・等級を管理します。
        </p>
        {!canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (登録・編集には全店舗管理権限が必要です)。</p>}
      </div>

      <div className="border-b border-surface-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="タブ">
          {tabs.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} aria-current={tab === key}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                tab === key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
              }`}>
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tabBody[tab]}
    </div>
  );
}
